import { useCallback, useState } from "react";

import { IconButton, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  GridCellParams,
  GridColDef,
  GridValueGetterParams,
  GridValueFormatterParams,
  GridFilterModel,
} from "@mui/x-data-grid";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import { isEqual } from "lodash";
import moment from "moment";
import { useUpdateEffect } from "react-use";

import DataGrid from "components/shared/XDataGrid";
import XNoRowsOverlay from "components/shared/XNoRowsOverlay";
import Pagination from "components/shared/Pagination";
import useAuth from "hooks/useAuth";
import useDataTable from "hooks/useXDataTable";
import useQuote from "hooks/useQuote";
import useWidth from "hooks/useWidth";
import { colors } from "theme";
import { IQuote } from "types/quote";
import { UserRole } from "types/user";

import { QuoteMenu } from "./QuoteMenu";

const useStyles = makeStyles((theme: Theme) => ({
  quotesTable: {
    backgroundColor: theme.palette.background.paper,
    border: "none",
    color: colors.nero[300],
    fontSize: 12,
    letterSpacing: 0.5,
    lineHeight: "17px",
  },
}));

export const DataTable = () => {
  const { currentPage, loading, refetch, quotes, totalCount, setCurrentPage } =
    useQuote();
  const width = useWidth();
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission([UserRole.Admin, UserRole.SalesRep]);
  const isCustomer = hasPermission([UserRole.Customer]);
  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "Date Requested",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) => {
        return moment(params.value as string).format("YYYY-MM-DD");
      },
    },
    {
      field: "requisitionId",
      headerName: "Requisition #",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "mpId",
      headerName: "Panel ID",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) => {
        return params.value?.toString().padStart(5, "0");
      },
    },
    {
      field: "workflow",
      headerName: "Workflow",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      filterable: isCustomer,
    },
    {
      field: "panelName",
      headerName: "Panel Name",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      filterable: isCustomer,
    },
    {
      field: "user",
      headerName: "Customer Name",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      filterable: isAdmin,
      valueGetter: (params: GridValueGetterParams) =>
        `${params?.row?.user?.firstName || ""} ${
          params?.row?.user?.lastName || ""
        }` || "",
    },
    {
      field: "id",
      headerName: "More",
      width: 66,
      hideSortIcons: true,
      disableColumnMenu: true,
      headerAlign: "right",
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        return (
          <IconButton
            edge="start"
            color="primary"
            size="small"
            onClick={(event: React.MouseEvent<HTMLElement>) =>
              openMenu(event, params.row as IQuote)
            }
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
  ];

  const { CustomToolbar, filter, filterButtonEl, setFilter } = useDataTable({
    columns,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<IQuote>(); // A state for quote id selected
  const classes = useStyles();

  const openMenu = (event: React.MouseEvent<HTMLElement>, quote: IQuote) => {
    setSelectedQuote(quote);
    setAnchorEl(event.currentTarget);
  };

  const filterModelChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!isEqual(filter, filterModel.items)) {
        setFilter({ ...filterModel });
      }
    },
    [filter, setFilter]
  );

  useUpdateEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useUpdateEffect(() => {
    refetch(currentPage, filter.items.length > 0 ? filter.items[0] : undefined);
  }, [filter, currentPage]);

  return (
    <>
      <DataGrid
        autoHeight
        className={classes.quotesTable}
        columns={columns}
        components={{
          NoRowsOverlay: () => <XNoRowsOverlay text="No Quotes" />,
          Pagination: () => (
            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          ),
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          panel: {
            anchorEl: filterButtonEl,
          },
        }}
        hideFooterSelectedRowCount
        hideFooterRowCount
        loading={loading}
        onPageChange={(page: number) => setCurrentPage(page)}
        onFilterModelChange={filterModelChange}
        filterModel={filter}
        page={currentPage - 1}
        pageSize={20}
        rowHeight={40}
        rows={quotes}
        columnVisibilityModel={{
          requisitionId: !["sm", "xs"].includes(width),
          workflow: isCustomer,
          user: isAdmin,
          panelName: isCustomer,
        }}
      />
      {!!selectedQuote && (
        <QuoteMenu
          anchorEl={anchorEl}
          selectedQuote={selectedQuote}
          setAnchorEl={setAnchorEl}
        />
      )}
    </>
  );
};
